import { type ComponentProps, useMemo } from "react"
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  Field,
  FieldLabel,
} from "../ui"
import FieldDescriptionOrError from "./FieldDescriptionOrError"

type Option = {
  id: string
  name: string
}

type Props = {
  options: Option[]
  onChange: (value: string | null) => void
  value: string | null
  label?: string
  placeholder?: string
  className?: string
  isError?: boolean
  error?: string | null
  helperText?: string
  empty?: string
  contentProps?: Omit<ComponentProps<typeof ComboboxContent>, "key">
}

export default function Autocomplete({
  options,
  onChange,
  value,
  label,
  placeholder,
  className,
  isError,
  error,
  helperText,
  empty = "No items available.",
  contentProps,
}: Props) {
  const selected = useMemo(
    () => options.find((option) => option.id === value) ?? null,
    [options, value],
  )

  return (
    <Field className={className}>
      {!!label && <FieldLabel>{label}</FieldLabel>}
      <Combobox
        items={options}
        value={selected}
        onValueChange={(option) => {
          onChange(option?.id ?? null)
        }}
        itemToStringLabel={({ name }) => name}
        itemToStringValue={({ id }) => id}
        isItemEqualToValue={(a, b) => a.id === b.id}
      >
        <ComboboxInput
          placeholder={placeholder ?? "Select..."}
          aria-invalid={isError ?? !!error}
        />
        <ComboboxContent {...contentProps}>
          <ComboboxEmpty>{empty}</ComboboxEmpty>
          <ComboboxList>
            {(option: Option) => (
              <ComboboxItem
                key={option.id}
                value={option}
                className="cursor-pointer"
              >
                {option.name}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      <FieldDescriptionOrError description={helperText} error={error} />
    </Field>
  )
}
